import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const inputCls = "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-orange-200 focus:border-orange-400";
const noAuto = { autoComplete: "off", autoCapitalize: "off", autoCorrect: "off", spellCheck: false };

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-700 flex items-center gap-2">
        {label}{hint && <span className="text-[10px] font-normal text-emerald-700">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

export function StorageB2ConfigForm({ cfg, setCfg, secretsSet, onTest, testing }) {
  const set = (k) => (e) => setCfg((c) => ({ ...c, [k]: e.target.value }));
  const kidLen = (cfg.key_id || "").trim().length;
  return (
    <div className="rounded-md border border-orange-200 bg-white p-3 space-y-3" data-testid="sa-b2-config">
      <p className="text-[11px] text-slate-500">
        Usa una <strong>Application Key dedicata al bucket</strong> (non la Master Application Key). Endpoint S3 es. <span className="font-mono">https://s3.eu-central-003.backblazeb2.com</span>, regione <span className="font-mono">eu-central-003</span>.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Endpoint S3">
          <input type="text" value={cfg.endpoint} onChange={set("endpoint")} placeholder="https://s3.eu-central-003.backblazeb2.com" className={inputCls} {...noAuto} data-testid="sa-b2-endpoint" />
        </Field>
        <Field label="Regione">
          <input type="text" value={cfg.region} onChange={set("region")} placeholder="eu-central-003" className={inputCls} {...noAuto} data-testid="sa-b2-region" />
        </Field>
        <Field label="Nome bucket">
          <input type="text" value={cfg.bucket_name} onChange={set("bucket_name")} placeholder="es. gestagepro-files" className={inputCls} {...noAuto} data-testid="sa-b2-bucket" />
        </Field>
        <Field label="Key ID" hint={secretsSet.key_id ? "✓ già impostata — lascia vuoto per non cambiarla" : null}>
          <input type="password" value={cfg.key_id} onChange={set("key_id")} placeholder={secretsSet.key_id ? "•••••••••••• (invariata)" : "003a1b2c3d4e5f60000000001"} className={inputCls} autoComplete="new-password" {...noAuto} data-testid="sa-b2-key-id" />
          {kidLen === 12 && (
            <p className="text-[11px] text-red-600 mt-1" data-testid="sa-b2-master-key-warning">
              ⚠️ Una Key ID di 12 caratteri è la Master Application Key, che B2 non accetta via S3: crea una Application Key dedicata al bucket.
            </p>
          )}
        </Field>
        <div className="sm:col-span-2">
          <Field label="Application Key" hint={secretsSet.application_key ? "✓ già impostata — lascia vuoto per non cambiarla" : null}>
            <input type="password" value={cfg.application_key} onChange={set("application_key")} placeholder={secretsSet.application_key ? "•••••••••••••••• (invariata)" : "K003…"} className={inputCls} autoComplete="new-password" {...noAuto} data-testid="sa-b2-application-key" />
          </Field>
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="button" variant="outline" size="sm" onClick={onTest} disabled={testing} data-testid="sa-b2-test-connection">
          {testing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : "🔌"} Verifica connessione
        </Button>
      </div>
      <p className="text-[10.5px] text-slate-500">La verifica usa la configurazione salvata: premi prima “Salva configurazione”.</p>
    </div>
  );
}

function MigrationTable({ res }) {
  return (
    <div className="overflow-x-auto mt-2">
      <table className="w-full text-xs" data-testid="sa-b2-migration-report">
        <thead><tr className="text-left text-slate-500"><th className="py-1 pr-2">Collezione</th><th>Campo</th><th>Totale</th><th>Già B2</th><th>Trovati su B2</th><th title="Stesso nome file ma percorso diverso: il percorso nel record verrà corretto">di cui percorso corretto</th><th>Ambigui</th><th>Mancanti</th>{!res.dry_run && <th>Aggiornati</th>}</tr></thead>
        <tbody>
          {res.collections.map((r) => (
            <tr key={`${r.collection}-${r.field}`} className="border-t border-slate-100" title={(r.missing_sample || []).join("\n")}>
              <td className="py-1 pr-2 font-mono">{r.collection}</td><td className="font-mono">{r.field}</td>
              <td>{r.total}</td><td>{r.already_b2}</td><td className="text-emerald-700 font-semibold">{r.found}</td><td className="text-sky-700">{r.found_remapped}</td><td className={r.ambiguous ? "text-amber-700" : ""}>{r.ambiguous}</td>
              <td className={r.missing ? "text-red-600 font-semibold" : ""}>{r.missing}</td>{!res.dry_run && <td>{r.updated}</td>}
            </tr>
          ))}
          <tr className="border-t-2 border-slate-300 font-bold">
            <td className="py-1" colSpan={2}>Totale</td><td>{res.totals.total}</td><td>{res.totals.already_b2}</td><td>{res.totals.found}</td><td>{res.totals.found_remapped}</td><td>{res.totals.ambiguous}</td><td>{res.totals.missing}</td>{!res.dry_run && <td>{res.totals.updated}</td>}
          </tr>
        </tbody>
      </table>
      <div className="mt-2 text-[11px] text-slate-600">File presenti sul bucket B2: <strong>{res.b2_keys_total}</strong></div>
      {res.diagnostics && (
        <details className="mt-1 text-[11px]" open={!res.totals.found} data-testid="sa-b2-migration-diagnostics">
          <summary className="cursor-pointer font-semibold text-slate-700">Confronto percorsi database ↔ bucket B2</summary>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
            <div><div className="font-semibold text-slate-500">Percorsi nel database</div>{res.diagnostics.db_path_samples.map((x) => <div key={x} className="font-mono break-all">{x}</div>)}</div>
            <div><div className="font-semibold text-slate-500">Chiavi sul bucket B2</div>{res.diagnostics.b2_key_samples.map((x) => <div key={x} className="font-mono break-all">{x}</div>)}</div>
          </div>
          {res.diagnostics.remap_samples?.length > 0 && (
            <div className="mt-1"><div className="font-semibold text-slate-500">Esempi di correzione percorso</div>
              {res.diagnostics.remap_samples.map((x) => <div key={x.db} className="font-mono break-all">{x.db} → <span className="text-sky-700">{x.b2}</span></div>)}
            </div>
          )}
        </details>
      )}
    </div>
  );
}

export function StorageB2MigrationPanel() {
  const [busy, setBusy] = useState(null);
  const [res, setRes] = useState(null);
  const [rb, setRb] = useState(null);

  const run = async (dry) => {
    if (!dry && !window.confirm("Collegare a B2 i record il cui file esiste già sul bucket B2? Nessun file viene cancellato da Wasabi. Puoi annullare con il rollback.")) return;
    setBusy(dry ? "dry" : "run");
    try {
      const { data } = await api.post(`/admin/storage/migrate-to-b2?dry_run=${dry}`, null, { timeout: 600000 });
      setRes(data); setRb(null);
      toast.success(dry ? `Simulazione: ${data.totals.found} file trovati su B2, ${data.totals.missing} mancanti` : `${data.totals.updated} record collegati a B2`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore migrazione"); }
    finally { setBusy(null); }
  };
  const rollback = async (dry) => {
    if (!dry && !window.confirm("Ripristinare il backend precedente per tutti i record migrati verso B2?")) return;
    setBusy(dry ? "rbdry" : "rb");
    try {
      const { data } = await api.post(`/admin/storage/migrate-to-b2/rollback?dry_run=${dry}`);
      setRb(data);
      toast.success(dry ? `${data.total_to_restore} record ripristinabili` : `Rollback completato (${data.total_to_restore} record)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore rollback"); }
    finally { setBusy(null); }
  };
  const spin = (k) => busy === k && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />;

  return (
    <div className="rounded-md border border-orange-200 bg-orange-50/30 p-3 space-y-2" data-testid="sa-b2-migration">
      <div className="text-sm font-semibold text-slate-800">🔁 Migrazione record Wasabi → B2</div>
      <p className="text-[11px] text-slate-600">
        Per ogni file registrato controlla se sul bucket B2 esiste la <strong>stessa chiave</strong>. Solo in quel caso il record viene collegato a B2 (il valore precedente viene salvato per il rollback). Nessun file viene copiato o cancellato da Wasabi.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run(true)} data-testid="sa-b2-migrate-dry">{spin("dry")}Simula (dry-run)</Button>
        <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" disabled={!!busy || !res?.dry_run || !res?.totals?.found} onClick={() => run(false)} data-testid="sa-b2-migrate-run">{spin("run")}Esegui migrazione</Button>
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => rollback(true)} data-testid="sa-b2-rollback-dry">{spin("rbdry")}Verifica rollback</Button>
        <Button size="sm" variant="outline" className="border-red-300 text-red-700" disabled={!!busy || !rb?.total_to_restore} onClick={() => rollback(false)} data-testid="sa-b2-rollback-run">{spin("rb")}Esegui rollback</Button>
      </div>
      {res && <MigrationTable res={res} />}
      {rb && <div className="text-xs text-slate-700" data-testid="sa-b2-rollback-report">Record ripristinabili: <strong>{rb.total_to_restore}</strong>{!rb.dry_run && " — ripristinati"}</div>}
    </div>
  );
}
